import { POI } from 'generated/mos/poi'
import { GetPOIResponse } from 'generated/mos/poiaccess'
import { CreatePOIRequest, UpdatePOIRequest, UpdatePOIResponse, DeletePOIRequest } from 'generated/mos/poimanagement'
import { assertNever, Invariant } from 'helpers/core';
import { ErrorMessage } from 'helpers/sagas'
import { Status, statusSelector } from 'helpers/status';

import { POIsList, POITypesList, POIFilterState, POIsAppState, initialPOIsAppState } from './store';

// exported for sagas, not to be bound to connected components.
export const actionCreators = {
  poisListPOITypesRequest: () => ({
    domain: 'pois' as const,
    type: 'poisListPOITypesRequest' as const,
  }),
  poisListPOITypesSuccess: (poiTypes: POITypesList) => ({
    domain: 'pois' as const,
    type: 'poisListPOITypesSuccess' as const,
    poiTypes,
  }),
  poisListPOITypesFailure: (message: ErrorMessage) => ({
    domain: 'pois' as const,
    type: 'poisListPOITypesFailure' as const,
    message,
  }),

  poisListPOIsRequest: () => ({
    domain: 'pois' as const,
    type: 'poisListPOIsRequest' as const,
  }),
  poisListPOIsSuccess: (pois: POIsList) => ({
    domain: 'pois' as const,
    type: 'poisListPOIsSuccess' as const,
    pois,
  }),
  poisListPOIsFailure: (message: ErrorMessage) => ({
    domain: 'pois' as const,
    type: 'poisListPOIsFailure' as const,
    message,
  }),

  poisGetPOIRequest: (ref: POI.Ref) => ({
    domain: 'pois' as const,
    type: 'poisGetPOIRequest' as const,
    ref,
  }),
  poisGetPOISuccess: (response: GetPOIResponse.Entity) => ({
    domain: 'pois' as const,
    type: 'poisGetPOISuccess' as const,
    poi: response.poi,
  }),
  poisGetPOIFailure: (poiRef: POI.Ref, message: ErrorMessage) => ({
    domain: 'pois' as const,
    type: 'poisGetPOIFailure' as const,
    poiRef,
    message,
  }),

  poisCreatePOIRequest: (payload: CreatePOIRequest.Entity) => ({
    domain: 'pois' as const,
    type: 'poisCreatePOIRequest' as const,
    payload,
  }),
  poisCreatePOISuccess: (ref: POI.Ref) => ({
    domain: 'pois' as const,
    type: 'poisCreatePOISuccess' as const,
    ref,
  }),
  poisCreatePOIFailure: (message: ErrorMessage) => ({
    domain: 'pois' as const,
    type: 'poisCreatePOIFailure' as const,
    message,
  }),

  poisUpdatePOIRequest: (payload: UpdatePOIRequest.Entity) => ({
    domain: 'pois' as const,
    type: 'poisUpdatePOIRequest' as const,
    payload,
  }),
  poisUpdatePOISuccess: (response: UpdatePOIResponse.Entity) => ({
    domain: 'pois' as const,
    type: 'poisUpdatePOISuccess' as const,
    poi: response.poi,
  }),
  poisUpdatePOIFailure: (poiRef: POI.Ref, message: ErrorMessage) => ({
    domain: 'pois' as const,
    type: 'poisUpdatePOIFailure' as const,
    message,
    poiRef,
  }),

  poisDeletePOIRequest: (poiRef: POI.Ref) => ({
    domain: 'pois' as const,
    type: 'poisDeletePOIRequest' as const,
    poiRef,
  }),
  poisDeletePOISuccess: (poiRef: POI.Ref) => ({
    domain: 'pois' as const,
    type: 'poisDeletePOISuccess' as const,
    poiRef,
  }),
  poisDeletePOIFailure: (poiRef: POI.Ref, message: ErrorMessage) => ({
    domain: 'pois' as const,
    type: 'poisDeletePOIFailure' as const,
    message,
    poiRef,
  }),

  poiFiltersUpdate: (updates: Partial<POIFilterState>) => ({
    domain: 'pois' as const,
    type: 'poiFiltersUpdate' as const,
    updates,
  }),
};

export type POIsActionCreators = typeof actionCreators;
export type POIsAction = ReturnType<typeof actionCreators[keyof typeof actionCreators]>;
export type POIsActions = {
  [T in keyof POIsActionCreators]: ReturnType<POIsActionCreators[T]>;
};

const assertPOIs = (domain: 'pois') => {
  return domain === 'pois'
}

export const poisReducer = (state: POIsAppState = initialPOIsAppState, action: POIsAction): POIsAppState => {
  if (!assertPOIs(action.domain)) {
    return state
  }

  switch (action.type) {
    // {{{ poi access sagas.
    case 'poisListPOITypesRequest': {
      return {
        ...state,
        poiTypes: {
          status: Status.Loading,
        },
      };
    }
    case 'poisListPOITypesSuccess': {
      return {
        ...state,
        poiTypes: {
          data: action.poiTypes,
          errors: [],
          status: Status.Ready,
        },
      };
    }
    case 'poisListPOITypesFailure': {
      return {
        ...state,
        poiTypes: {
          messages: [action.message],
          status: Status.Failed,
        },
      };
    }

    case 'poisListPOIsRequest': {
      return {
        ...state,
        pois: {
          status: Status.Loading,
        },
        poiCreate: {
          status: Status.Idle,
        },
        poiEdit: {
          status: Status.Idle,
        },
      };
    }
    case 'poisListPOIsSuccess': {
      return {
        ...state,
        pois: {
          data: action.pois,
          errors: [],
          status: Status.Ready,
        },
      };
    }
    case 'poisListPOIsFailure': {
      return {
        ...state,
        pois: {
          status: Status.Failed,
          messages: [action.message],
        },
      };
    }

    case 'poisGetPOIRequest': {
      return {
        ...state,
        poiEdit: {
          ref: action.ref,
          status: Status.Loading,
        },
      };
    }
    case 'poisGetPOISuccess': {
      if (!action.poi || !action.poi.ref) {
        throw new Invariant('Get POI responded successfully without returning a POI')
      }
      return {
        ...state,
        poiEdit: {
          data: action.poi,
          errors: [],
          ref: POI.mustRef(action!.poi.ref),
          status: Status.Ready,
        },
      };
    }
    case 'poisGetPOIFailure': {
      return {
        ...state,
        poiEdit: {
          ref: action.poiRef,
          status: Status.Failed,
          messages: [action.message],
        },
      };
    }
    // }}} poi access sagas.

    // {{{ poi management sagas.
    case 'poisCreatePOIRequest': {
      return {
        ...state,
        poiCreate: {
          status: Status.Loading,
        },
      };
    }
    case 'poisCreatePOISuccess': {
      return {
        ...state,
        pois: statusSelector.invalidate(state.pois),
        poiCreate: {
          data: action.ref,
          errors: [],
          status: Status.Ready,
        },
      };
    }
    case 'poisCreatePOIFailure': {
      return {
        ...state,
        poiCreate: {
          messages: [action.message],
          status: Status.Failed,
        },
      };
    }

    case 'poisUpdatePOIRequest': {
      if (state.poiEdit.status !== Status.Ready) {
        throw new Invariant(`poiEdit tried to edit ${action.payload.poiRef!.id} while ${state.poiEdit.status}`);
      }
      return {
        ...state,
        poiEdit: {
          data: state.poiEdit.data,
          ref: POI.mustRef(action.payload.poiRef),
          status: Status.Updating,
        },
      };
    }
    case 'poisUpdatePOISuccess': {
      if (!action.poi || !action.poi.ref) {
        throw new Invariant('Update POI responded successfully without returning a POI')
      }
      return {
        ...state,
        pois: statusSelector.invalidate(state.pois),
        poiEdit: {
          ref: POI.mustRef(action.poi.ref),
          status: Status.Complete,
          message: 'Point of interest saved.',
        },
      };
    }
    case 'poisUpdatePOIFailure': {
      return { ...state, poiEdit: {
        messages: [ action.message ],
        ref: action.poiRef,
        status: Status.Failed,
      }};
    }

    case 'poisDeletePOIRequest': {
      if (state.poiEdit.status !== Status.Ready) {
        throw new Invariant(`Tried to delete POI ${action.poiRef} while ${state.poiEdit.status}`);
      }
      return {
        ...state,
        poiEdit: {
          status: Status.Updating,
          ref: action.poiRef,
          data: state.poiEdit.data,
        },
      };
    }
    case 'poisDeletePOISuccess': {
      return {
        ...state,
        poiEdit: {
          ref: action.poiRef,
          status: Status.Complete,
          message: 'Point of interest deleted.',
        },
      };
    }
    case 'poisDeletePOIFailure': {
      return {
        ...state,
        poiEdit: {
          messages: [action.message],
          status: Status.Failed,
          ref: action.poiRef,
        },
      };
    }
    // }}} poi management sagas.

    case 'poiFiltersUpdate': {
      return {
        ...state,
        poiFilters: {
          ...state.poiFilters,
          ...action.updates,
        },
      };
    }

    default: {
      assertNever(action);
      throw new Invariant();
    }
  }
};
